import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  LinearProgress,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CloudUpload as CloudUploadIcon,
  Send as SendIcon,
  Lock as LockIcon,
  GppGood as GppGoodIcon,
  GppBad as GppBadIcon,
  GppMaybe as GppMaybeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import {
  getAssessmentByToken,
  updateVendorResponse,
  uploadEvidence,
  submitVendorAssessment
} from 'services/vendor-assessment-service';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

const getScanStatusDisplay = (scanStatus, t) => {
  if (!scanStatus) {
    return {
      icon: <GppGoodIcon fontSize="small" />,
      color: 'success.main',
      label: t('gap-analysis:assessment.vendorForm.scanStatus.verified'),
      chipColor: 'success'
    };
  }
  switch (scanStatus) {
    case 'clean':
      return {
        icon: <GppGoodIcon fontSize="small" />,
        color: 'success.main',
        label: t('gap-analysis:assessment.vendorForm.scanStatus.scanPassed'),
        chipColor: 'success'
      };
    case 'malicious':
      return {
        icon: <GppBadIcon fontSize="small" />,
        color: 'error.main',
        label: t('gap-analysis:assessment.vendorForm.scanStatus.threatDetected'),
        chipColor: 'error'
      };
    case 'error':
      return {
        icon: <GppMaybeIcon fontSize="small" />,
        color: 'warning.main',
        label: t('gap-analysis:assessment.vendorForm.scanStatus.scanError'),
        chipColor: 'warning'
      };
    case 'pending':
    default:
      return {
        icon: <GppMaybeIcon fontSize="small" />,
        color: 'text.secondary',
        label: t('gap-analysis:assessment.vendorForm.scanStatus.scanning'),
        chipColor: 'default'
      };
  }
};


/**
 * VendorAssessmentForm Component
 * Public vendor assessment submission interface (token-based, no authentication)
 */
export const VendorAssessmentForm = () => {
  const { t } = useTranslation(['gap-analysis']);
  const { token } = useParams();

  const maturityLevels = [
    { value: 0, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l0.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l0.description') },
    { value: 1, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l1.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l1.description') },
    { value: 2, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l2.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l2.description') },
    { value: 3, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l3.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l3.description') },
    { value: 4, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l4.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l4.description') },
    { value: 5, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l5.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l5.description') },
  ];
  const queryClient = useQueryClient();
  const SwalReact = withReactContent(Swal);

  const [expandedDomain, setExpandedDomain] = useState(false);
  const [localNotes, setLocalNotes] = useState({});
  const debounceTimers = useRef({});

  // Fetch assessment data (includes both assessment and controlResponses)
  const { data: assessmentData, isLoading: assessmentLoading } = useQuery(
    ['vendor-assessment', token],
    () => getAssessmentByToken(token),
    {
      enabled: !!token,
      retry: false,
      onError: () => {
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.vendorForm.swal.invalidTitle'),
          text: t('gap-analysis:assessment.vendorForm.swal.invalidText')
        });
      }
    }
  );

  // Extract assessment and control responses from single API call
  const assessment = assessmentData?.data?.data?.assessment;
  const controls = assessmentData?.data?.data?.controlResponses || [];

  // Group controls by domain
  const controlsByDomain = React.useMemo(() => {
    const grouped = {};
    controls.forEach(control => {
      if (!grouped[control.domain]) {
        grouped[control.domain] = [];
      }
      grouped[control.domain].push(control);
    });
    return grouped;
  }, [controls]);

  // Calculate progress
  const progress = React.useMemo(() => {
    if (controls.length === 0) return 0;
    const answered = controls.filter(c => c.response?.maturityLevel !== undefined).length;
    return Math.round((answered / controls.length) * 100);
  }, [controls]);

  // Initialize local notes from server data when controls load
  useEffect(() => {
    if (controls.length > 0) {
      setLocalNotes(prev => {
        const next = { ...prev };
        controls.forEach(control => {
          if (control.response?._id && !(control.response._id in next)) {
            next[control.response._id] = control.response?.notes || '';
          }
        });
        return next;
      });
    }
  }, [controls]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  // Update response mutation (for maturity level changes — invalidates queries)
  const updateResponseMutation = useMutation(
    ({ controlResponseId, maturityLevel, notes }) =>
      updateVendorResponse(token, controlResponseId, { maturityLevel, notes }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendor-assessment', token]);
      },
      onError: () => {
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.vendorForm.swal.errorTitle'),
          text: t('gap-analysis:assessment.vendorForm.swal.errorSaveResponse')
        });
      }
    }
  );

  // Separate mutation for notes (no query invalidation to avoid resetting text)
  const updateNotesMutation = useMutation(
    ({ controlResponseId, notes }) =>
      updateVendorResponse(token, controlResponseId, { notes }),
    {
      onError: () => {
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.vendorForm.swal.errorTitle'),
          text: t('gap-analysis:assessment.vendorForm.swal.errorSaveNotes')
        });
      }
    }
  );

  // File upload mutation
  const uploadMutation = useMutation(
    (formData) => uploadEvidence(token, formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendor-assessment', token]);
        SwalReact.fire({
          icon: 'info',
          title: t('gap-analysis:assessment.vendorForm.swal.uploadedTitle'),
          text: t('gap-analysis:assessment.vendorForm.swal.uploadedText'),
          timer: 2000,
          showConfirmButton: false
        });
      },
      onError: (error) => {
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.vendorForm.swal.uploadFailedTitle'),
          text: error.response?.data?.message || t('gap-analysis:assessment.vendorForm.swal.uploadFailedTitle')
        });
      }
    }
  );

  // Submit assessment mutation
  const submitMutation = useMutation(
    () => submitVendorAssessment(token),
    {
      onSuccess: () => {
        SwalReact.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.vendorForm.swal.submittedTitle'),
          html: `<p>${t('gap-analysis:assessment.vendorForm.swal.submittedHtml')}</p>`,
          confirmButtonText: 'Close'
        });
      },
      onError: (error) => {
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.vendorForm.swal.submitFailedTitle'),
          text: error.response?.data?.message || t('gap-analysis:assessment.vendorForm.swal.submitFailedText')
        });
      }
    }
  );

  const handleMaturityChange = (controlResponseId, maturityLevel) => {
    updateResponseMutation.mutate({ controlResponseId, maturityLevel });
  };

  const handleNotesChange = useCallback((controlResponseId, notes) => {
    setLocalNotes(prev => ({ ...prev, [controlResponseId]: notes }));

    if (debounceTimers.current[controlResponseId]) {
      clearTimeout(debounceTimers.current[controlResponseId]);
    }
    debounceTimers.current[controlResponseId] = setTimeout(() => {
      updateNotesMutation.mutate({ controlResponseId, notes });
    }, 800);
  }, [updateNotesMutation]);

  const handleFileUpload = (controlResponseId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('controlResponseId', controlResponseId);

    uploadMutation.mutate(formData);
  };

  const handleSubmit = async () => {
    const result = await SwalReact.fire({
      title: t('gap-analysis:assessment.vendorForm.swal.submitTitle'),
      html: `<p>${t('gap-analysis:assessment.vendorForm.swal.submitHtml', { progress })}</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('gap-analysis:assessment.vendorForm.swal.submitButton'),
    });

    if (result.isConfirmed) {
      submitMutation.mutate();
    }
  };

  const handleAccordionChange = (domain) => (event, isExpanded) => {
    setExpandedDomain(isExpanded ? domain : false);
  };

  if (assessmentLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!assessment) {
    return (
      <Container maxWidth="md">
        <Box py={8} textAlign="center">
          <Typography variant="h5" color="error" gutterBottom>
            {t('gap-analysis:assessment.vendorForm.invalidLink')}
          </Typography>
          <Typography color="textSecondary">
            {t('gap-analysis:assessment.vendorForm.invalidLinkText')}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        {/* Header */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h4" gutterBottom>
              {t('gap-analysis:assessment.vendorForm.title')}
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              {t('gap-analysis:assessment.vendorForm.requestedBy')} <strong>{assessment.companyName}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('gap-analysis:assessment.vendorForm.framework')} {assessment.frameworkName}
            </Typography>

            {/* Progress Bar */}
            <Box mt={3}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">{t('gap-analysis:assessment.vendorForm.progress')}</Typography>
                <Typography variant="body2" fontWeight="bold">{progress}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
            </Box>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('gap-analysis:assessment.vendorForm.instructions')}
          </Typography>
        </Alert>

        {/* Control Questions by Domain */}
        {Object.entries(controlsByDomain).map(([domain, domainControls]) => {
          const domainAnswered = domainControls.filter(c => c.response?.maturityLevel !== undefined).length;
          const domainTotal = domainControls.length;
          const domainProgress = Math.round((domainAnswered / domainTotal) * 100);

          return (
            <Accordion
              key={domain}
              expanded={expandedDomain === domain}
              onChange={handleAccordionChange(domain)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Typography variant="h6">{domain}</Typography>
                  <Chip
                    label={`${domainAnswered}/${domainTotal}`}
                    color={domainProgress === 100 ? 'success' : 'default'}
                    size="small"
                    sx={{ mr: 2 }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {domainControls.map((control, index) => (
                  <Box key={control._id} mb={4}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {index + 1}. {control.description}
                      </Typography>
                      {control.response?.locked && (
                        <Chip icon={<LockIcon />} label="Locked" size="small" variant="outlined" sx={{ ml: 1 }} />
                      )}
                    </Box>

                    {/* Maturity Level Selection */}
                    <RadioGroup
                      value={control.response?.maturityLevel ?? ''}
                      onChange={(e) => handleMaturityChange(control.response._id, parseInt(e.target.value))}
                    >
                      {maturityLevels.map((level) => (
                        <FormControlLabel
                          key={level.value}
                          value={level.value}
                          control={<Radio />}
                          disabled={control.response?.locked}
                          label={
                            <Box>
                              <Typography variant="body2">{level.label}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {level.description}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>

                    {/* Notes Field */}
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label={t('gap-analysis:assessment.vendorForm.additionalNotes')}
                      value={localNotes[control.response?._id] ?? control.response?.notes ?? ''}
                      onChange={(e) => handleNotesChange(control.response._id, e.target.value)}
                      disabled={control.response?.locked}
                      sx={{ mt: 2 }}
                    />

                    {/* Evidence Upload */}
                    {control.evidenceRequired && (
                      <Box mt={2}>
                        <Typography variant="body2" gutterBottom>
                          {t('gap-analysis:assessment.vendorForm.evidenceRequired')}
                        </Typography>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<CloudUploadIcon />}
                          disabled={uploadMutation.isLoading || control.response?.locked}
                        >
                          {t('gap-analysis:assessment.vendorForm.uploadEvidence')}
                          <input
                            type="file"
                            hidden
                            accept=".pdf,.png,.jpg,.jpeg"
                            onChange={(e) => handleFileUpload(control.response._id, e)}
                          />
                        </Button>

                        {control.response?.evidenceFiles?.some(f => f.scanStatus === 'pending') && (
                          <Tooltip title="Refresh to check scan results">
                            <Button
                              size="small"
                              startIcon={<RefreshIcon />}
                              onClick={() => queryClient.invalidateQueries(['vendor-assessment', token])}
                              sx={{ ml: 1 }}
                            >
                              {t('gap-analysis:assessment.vendorForm.checkScanStatus')}
                            </Button>
                          </Tooltip>
                        )}

                        {/* Uploaded Files List */}
                        {control.response?.evidenceFiles && control.response.evidenceFiles.length > 0 && (
                          <List dense>
                            {control.response.evidenceFiles.map((file) => {
                              const scanDisplay = getScanStatusDisplay(file.scanStatus, t);
                              return (
                                <ListItem key={file._id}>
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Tooltip title={scanDisplay.label}>
                                      <Box component="span" sx={{ color: scanDisplay.color, display: 'flex' }}>
                                        {scanDisplay.icon}
                                      </Box>
                                    </Tooltip>
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={file.fileName}
                                    secondary={
                                      file.scanStatus === 'malicious'
                                        ? `${t('gap-analysis:assessment.vendorForm.scanStatus.threatDetected')}${file.scanResultDetail ? ': ' + file.scanResultDetail : ''}`
                                        : file.scanStatus === 'pending'
                                          ? t('gap-analysis:assessment.vendorForm.scanStatus.scanning')
                                          : `${(file.fileSize / 1024).toFixed(1)} KB`
                                    }
                                    secondaryTypographyProps={
                                      file.scanStatus === 'malicious' ? { color: 'error' } : undefined
                                    }
                                  />
                                  <Chip
                                    label={scanDisplay.label}
                                    color={scanDisplay.chipColor}
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                        )}
                      </Box>
                    )}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}

        {/* Action Buttons */}
        <Box display="flex" gap={2} justifyContent="flex-end" alignItems="center" mt={4}>
          {(updateResponseMutation.isLoading || updateNotesMutation.isLoading) && (
            <Typography variant="body2" color="textSecondary">{t('gap-analysis:assessment.vendorForm.saving')}</Typography>
          )}
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSubmit}
            disabled={submitMutation.isLoading || progress < 100}
          >
            {submitMutation.isLoading ? <CircularProgress size={24} /> : t('gap-analysis:assessment.vendorForm.submitButton')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};
